<template>
  <header :class="['app-header', { 'header-compact': scrolled }]">
    <div class="header-content">
      <div class="header-left">
        <a href="#" class="logo-link" tabindex="-1" :style="{ backgroundImage: `url('${publicPath}img/mp_live.svg')` }" @click.prevent="goHome">
          <span class="sr-only">{{ $t('header.home') }}</span>
        </a>
        <h1 class="mb-0">{{ $t('app.title') }}</h1>
      </div>
      <div class="header-right">
        <div class="entity-toggle" role="radiogroup" :aria-label="$t('header.entityType')">
          <button
            :class="['toggle-btn', { active: !isCharity }]"
            role="radio"
            :aria-checked="String(!isCharity)"
            @click="setEntityType(false)"
          >
            {{ $t('header.commercial') }}
          </button>
          <button
            :class="['toggle-btn', { active: isCharity }]"
            role="radio"
            :aria-checked="String(isCharity)"
            @click="setEntityType(true)"
          >
            {{ $t('header.charity') }}
          </button>
        </div>
        <div
          class="home-dropdown"
          @mouseenter="showHomeMenu = true"
          @mouseleave="showHomeMenu = false"
        >
          <a
            href="#"
            class="header-nav-link home-link"
            @click.prevent="handleHomeClick"
          >
            <i class="fas fa-home" aria-hidden="true"></i>
            <span class="text-uppercase d-none d-lg-inline">{{ $t('header.home') }}</span>
            <i class="fas fa-chevron-down home-chevron" aria-hidden="true"></i>
          </a>
          <div v-show="showHomeMenu" class="home-submenu">
            <a
              href="https://marathonphotos.live/"
              class="home-submenu-item"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('header.mainSite') }}
              <i class="fas fa-external-link-alt" aria-hidden="true"></i>
            </a>
          </div>
        </div>
        <router-link to="/cart" class="header-nav-link cart-link">
          <i class="fas fa-shopping-cart" aria-hidden="true"></i>
          <span class="text-uppercase d-none d-lg-inline">{{ $t('header.cart') }}</span>
          <span v-if="cartItemCount" class="text-uppercase d-none d-lg-inline">({{ cartItemCount }})</span>
          <span v-if="cartItemCount" class="d-inline d-lg-none">({{ cartItemCount }})</span>
        </router-link>
        <b-dropdown
          class="lang-dropdown"
          :aria-label="$t('app.changeLanguage')"
          variant="link"
          no-caret
          right
        >
          <template #button-content>
            <span class="fi lang-flag" :class="'fi-' + currentCountryCode"></span>
            <span class="lang-title d-none d-lg-inline">{{ currentTitle }}</span>
            <i class="fas fa-chevron-down lang-chevron" aria-hidden="true"></i>
          </template>
          <b-dropdown-item-button
            v-for="lang in languages"
            :key="lang.code"
            :aria-current="lang.code === $i18n.locale ? 'true' : undefined"
            :class="{ active: lang.code === $i18n.locale }"
            @click="changeLanguage(lang)"
          >
            <span class="fi lang-flag" :class="'fi-' + lang.countryCode"></span>
            {{ lang.title }}
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
import { languages, loadLocale } from '@/i18n'

export default {
  name: 'AppHeader',
  data() {
    return {
      languages,
      scrolled: false,
      showHomeMenu: false,
      publicPath: process.env.BASE_URL
    }
  },
  computed: {
    isCharity() {
      return this.$store.getters.isCharity
    },
    cartItemCount() {
      if (this.$store && this.$store.getters) {
        return this.$store.getters.cartItemCount || 0
      }
      return 0
    },
    currentCountryCode() {
      const lang = this.languages.find(l => l.code === this.$i18n.locale)
      return lang ? lang.countryCode : 'nz'
    },
    currentTitle() {
      const lang = this.languages.find(l => l.code === this.$i18n.locale)
      return lang ? lang.title : ''
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
    document.addEventListener('click', this.closeHomeMenu)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    document.removeEventListener('click', this.closeHomeMenu)
  },
  created() {
    const saved = localStorage.getItem('mp-locale')
    if (saved && this.languages.some(l => l.code === saved)) {
      this.loadAndApply(saved)
    } else {
      const browserLang = (navigator.language || '').split('-')[0]
      if (browserLang !== 'en' && this.languages.some(l => l.code === browserLang)) {
        this.loadAndApply(browserLang)
      }
    }
  },
  methods: {
    /**
     * Toggle compact header state based on scroll position.
     */
    onScroll() {
      this.scrolled = window.innerWidth >= 992 && window.scrollY > 100
    },
    /**
     * Switch the active locale, persist to localStorage, and update document lang.
     * @param {Object} lang - Language object with code property
     */
    /**
     * Set the entity type (commercial or charity) and update pricing.
     * @param {boolean} isCharity - Whether the user represents a charity
     */
    setEntityType(isCharity) {
      this.$store.dispatch('setIsCharity', isCharity)
    },
    changeLanguage(lang) {
      this.loadAndApply(lang.code)
      localStorage.setItem('mp-locale', lang.code)
    },
    /**
     * Navigate home and reset search state.
     * If already on home, clears event/bib search without a page refresh.
     */
    /**
     * Close home submenu when clicking outside.
     * @param {MouseEvent} e
     */
    closeHomeMenu(e) {
      if (this.showHomeMenu && !this.$el.querySelector('.home-dropdown')?.contains(e.target)) {
        this.showHomeMenu = false
      }
    },
    /**
     * Handle Home link click. On touch devices, toggle the submenu.
     * On desktop (where hover already shows it), navigate home.
     */
    handleHomeClick() {
      if ('ontouchstart' in window) {
        this.showHomeMenu = !this.showHomeMenu
      } else {
        this.goHome()
      }
    },
    goHome() {
      this.showHomeMenu = false
      this.$store.dispatch('resetSearch')
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    },
    /**
     * Load a locale's messages on demand, then apply it.
     * @param {string} code - ISO language code
     * @returns {Promise<void>}
     */
    async loadAndApply(code) {
      await loadLocale(code)
      document.documentElement.lang = code
    }
  }
}
</script>

<style scoped>
.app-header {
  background-color: var(--mp-teal);
  padding: 0.75rem 0;
  border-bottom: 1px solid #000;
  position: sticky;
  top: 0;
  z-index: 1030;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  transition: max-width 0.5s cubic-bezier(0.92, 0, 0.8, 1);
}

.header-compact .header-content {
  padding-right: 8.5px;
}

@media (min-width: 576px) {
  .header-compact .header-content {
    max-width: 540px;
  }
}

@media (min-width: 768px) {
  .header-compact .header-content {
    max-width: 720px;
  }
}

@media (min-width: 992px) {
  .header-compact .header-content {
    max-width: 960px;
  }
}

@media (min-width: 1200px) {
  .header-compact .header-content {
    max-width: 1140px;
  }
}


.logo-link {
  display: block;
  margin-left: 1.2rem;
  border-radius: var(--border-radius-sm);
  background-size: auto 100%;
  background-position: left center;
  background-repeat: no-repeat;
  width: 13.3rem;
  height: 2.875rem;
  margin-bottom: 0.2rem;
  transition: width 0.5s cubic-bezier(0.92, 0, 0.8, 1),
              margin-left 0.5s cubic-bezier(0.92, 0, 0.8, 1);
}

.logo-link:focus {
  outline: none;
}

.app-header h1 {
  color: var(--mp-blue);
  font-size: 1.5rem;
  font-weight: 600;
  text-align: left;
}

.header-nav-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  color: var(--mp-blue);
  text-decoration: none;
  font-weight: 500;
  font-size: 1.1rem;
}

.header-nav-link:hover {
  text-decoration: none;
}

.home-dropdown {
  position: relative;
}

.home-link {
  position: relative;
}

.home-chevron {
  font-size: 0.5rem;
  opacity: 0.6;
  margin-left: -0.25rem;
}

.home-submenu {
  position: absolute;
  top: calc(100% - 0.5rem);
  left: 50%;
  transform: translateX(calc(-50% + 2px));
  background: #fff;
  border: 1px solid var(--mp-blue);
  border-top: none;
  border-radius: 0 0 var(--border-radius-sm) var(--border-radius-sm);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  padding: 0.25rem 0;
  z-index: 1040;
}

.home-submenu-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  color: var(--mp-blue);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}

.home-submenu-item:hover {
  background-color: rgba(44, 0, 133, 0.1);
  color: var(--mp-blue);
  text-decoration: none;
}

.home-submenu-item .fa-external-link-alt {
  font-size: 0.7rem;
  opacity: 0.5;
}

.header-nav-link:focus {
  outline: none;
  background-color: rgba(44, 0, 133, 0.1);
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-right {
  margin-left: auto;
}

.header-compact .logo-link {
  width: 2.4rem;
  margin-left: 0;
}

.lang-dropdown >>> .btn-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  color: var(--mp-blue);
  text-decoration: none;
  font-weight: 500;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}

.lang-dropdown >>> .btn-link:hover,
.lang-dropdown >>> .btn-link:focus {
  background-color: rgba(44, 0, 133, 0.1);
  border-color: transparent;
}

.lang-dropdown >>> .btn-link:focus {
  outline: none;
  box-shadow: none;
}

.lang-flag {
  width: 1.25em;
  line-height: 1;
}

.lang-title {
  font-size: 1.1rem;
  text-transform: uppercase;
}

.lang-chevron {
  font-size: 0.625rem;
}

.lang-dropdown >>> .dropdown-menu {
  min-width: 10rem;
}

.lang-dropdown >>> .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
}

.lang-dropdown >>> .dropdown-item.active,
.lang-dropdown >>> .dropdown-item:active {
  background-color: var(--mp-blue);
  color: #fff;
}

.lang-dropdown {
  margin-right: 1.2rem;
  transition: margin-right 0.5s cubic-bezier(0.92, 0, 0.8, 1);
}

.header-compact .lang-dropdown {
  margin-right: 0;
}

.entity-toggle {
  display: flex;
  background-color: rgba(44, 0, 133, 0.12);
  border-radius: 1.25rem;
  padding: 0.15rem;
  gap: 0.125rem;
}

.toggle-btn {
  border: none;
  background: transparent;
  color: var(--mp-blue);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 1rem;
  cursor: pointer;
  white-space: nowrap;
  text-transform: uppercase;
  transition: background-color 0.2s, color 0.2s;
  line-height: 1.2;
}

.toggle-btn:hover {
  background-color: rgba(44, 0, 133, 0.08);
}

.toggle-btn.active {
  background-color: var(--mp-blue);
  color: #fff;
}

.toggle-btn:focus {
  outline: 2px solid var(--mp-blue);
  outline-offset: 1px;
}

@media (min-width: 768px) and (max-width: 991px) {
  .logo-link {
    width: 2.4rem;
    margin-left: 0.5rem;
  }

  .app-header h1 {
    font-size: 1rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-left {
    min-width: 0;
  }

  .header-nav-link {
    padding: 0.4rem 0.5rem;
    font-size: 1rem;
  }

  .toggle-btn {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }

  .lang-dropdown {
    margin-right: 0.5rem;
  }
}

@media (max-width: 767px) {
  .header-content {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .logo-link {
    width: 2.4rem;
    margin-left: 0.5rem;
  }

  .app-header h1 {
    font-size: 0.85rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-left {
    gap: 0.25rem;
    min-width: 0;
  }

  .header-right {
    width: 100%;
    justify-content: flex-end;
    gap: 0.25rem;
    padding: 0 0.5rem;
  }

  .header-nav-link {
    padding: 0.4rem;
    font-size: 0.9rem;
  }

  .toggle-btn {
    font-size: 0.6rem;
    padding: 0.2rem 0.4rem;
  }

  .entity-toggle {
    padding: 0.1rem;
  }

  .lang-dropdown {
    margin-right: 0.25rem;
  }
}
</style>
